'use client';

import type { LogoutReason, RefreshPayload } from '@tracedocuments/types';
import { BrowserTokenStorage, type ITokenStorage } from './storage';

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

// ---------------------------------------------------------------------------
// Platform-agnostic event emitter for auth logout.
// `window.addEventListener` does not exist in React Native (Hermes/JSC),
// so we use a simple listener set instead.
// ---------------------------------------------------------------------------

type LogoutListener = (reason: LogoutReason) => void;
const _logoutListeners = new Set<LogoutListener>();

function emitLogout(reason: LogoutReason): void {
    _logoutListeners.forEach((fn) => fn(reason));
}

// ---------------------------------------------------------------------------
// Configurable storage — default to BrowserTokenStorage (localStorage).
// Mobile apps call `setTokenStorage(new SecureTokenStorage())` at startup.
// ---------------------------------------------------------------------------

let _storage: ITokenStorage = new BrowserTokenStorage();

export function setTokenStorage(storage: ITokenStorage): void {
    _storage = storage;
}

export function getTokenStorage(): ITokenStorage {
    return _storage;
}

// ---------------------------------------------------------------------------
// Configurable API URL — defaults to NEXT_PUBLIC_API_URL env or localhost.
// Mobile apps call `setAuthApiUrl(...)` at startup.
// ---------------------------------------------------------------------------

let _apiUrl: string | null = null;
let _clientPlatformHeader: string | null = null;

export function setAuthApiUrl(url: string): void {
    _apiUrl = url;
}

/** Mirrors @tracedocuments/api client platform for refresh / MFA login fetches. */
export function setAuthClientPlatform(value: string | null): void {
    _clientPlatformHeader = value;
}

export function getAuthClientPlatform(): string | null {
    return _clientPlatformHeader;
}

function getApiUrl(): string {
    return _apiUrl ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:5090';
}

function withAuthClientHeaders(headers: Record<string, string>): Record<string, string> {
    if (_clientPlatformHeader) {
        return { ...headers, 'X-TraceDocuments-Client-Platform': _clientPlatformHeader };
    }
    return headers;
}

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<string | null> | null = null;

export function getAuthToken(): string | null {
    return _storage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
    return _storage.getItem(REFRESH_TOKEN_KEY);
}

export function setAuthSession(accessToken: string, refreshToken: string): void {
    _storage.setItem(ACCESS_TOKEN_KEY, accessToken);
    _storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function decodeTokenPayload(token: string): Record<string, unknown> | null {
    try {
        const payloadBase64 = token.split('.')[1];
        if (!payloadBase64) return null;

        return JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    } catch {
        return null;
    }
}

export function getUserIdFromToken(): string | null {
    const token = getAuthToken();
    if (!token) return null;

    const payload = decodeTokenPayload(token);
    const sub = payload?.sub;
    return typeof sub === 'string' && sub.length > 0 ? sub : null;
}

export function isTokenExpired(token: string, marginMs = 60_000): boolean {
    const payload = decodeTokenPayload(token);
    if (!payload?.exp) return true;

    return Date.now() >= (payload.exp as number) * 1000 - marginMs;
}

export function clearAuthToken(reason: LogoutReason): void {
    _storage.removeItem(ACCESS_TOKEN_KEY);
    _storage.removeItem(REFRESH_TOKEN_KEY);
    emitLogout(reason);
}

export function onAuthLogout(listener: (reason: LogoutReason) => void): () => void {
    _logoutListeners.add(listener);
    return () => { _logoutListeners.delete(listener); };
}

export async function refreshSession(): Promise<string | null> {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return null;

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15_000);
            const response = await fetch(`${getApiUrl()}/api/auth/refresh`, {
                method: 'POST',
                headers: withAuthClientHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ refreshToken }),
                signal: controller.signal,
            }).finally(() => clearTimeout(timeoutId));

            if (!response.ok) return null;

            const payload = (await response.json()) as RefreshPayload;
            if (!payload.token || !payload.refreshToken) return null;

            setAuthSession(payload.token, payload.refreshToken);
            return payload.token;
        } catch {
            return null;
        }
    })();

    try {
        return await refreshInFlight;
    } finally {
        refreshInFlight = null;
    }
}

// Re-export types so consumers can import from @tracedocuments/auth directly
export type { LogoutReason, RefreshPayload } from '@tracedocuments/types';
